// ------------- import external dependencies ------------
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineClose } from "react-icons/ai";
import { useFormik } from "formik";
import { toast } from "react-toastify";

// ----------- import internal dependencies -----------
import FormInput from "../../../../../components/Forms/input/FormInput";
import Button from "../../../../../components/Button";
import Modal from "../../../../../components/Modal";

import { UserCreation } from "../../type";
import { validateAssignment } from "../../../../../utils/validators";
import { useStoreWrapper } from "../../../../../store/store";
import useAssignment from "../../../../../hooks/useAssignment";
import AccordionInput from "./AccordionInput";
import { PERMISSION_LIST } from "../../../../../shared/sharedConstants";
import useUser from "../../../hooks/useUser";
import {
  convertObjEntriesToArray,
  toastConfig,
  customStyles,
  filterFacilities,
  filterGuestProfile,
  getPermissionObject,
} from "../../../../../utils/helpers";
// ------ import assets -------
import { BsPlusLg } from "react-icons/bs";
// ------- import styles -------
import {
  ActionBtnContainer,
  AssModal,
  FormInputContainer,
  InputandButtonContainer,
  RolesFormWrapper,
  StyledRolesMultiSelect,
} from "./style";
import { usePermission } from "../../../../../hooks/usePermission";

function Roles({ cb, back }: UserCreation) {
  // ------- application state managers --------
  const [open, setOpen] = useState(false);
  const [assignmentPermissions, setAssignmentPermissions] = useState<any>({});
  const [permissionList, setPermissionList] = useState<any>({});
  const [modalTitle, setModalTitle] = useState("");
  const [profileId, setProfileId] = useState("");

  // ------- intialize custom hooks --------
  const {
    user,
    edit,
    assignments,
    allowAddAssign,
    editIndex,
    clearState,
    updateField,
    setSelectedUserFacilities,
    addNewAssignment,
    updateAssignment,
    editAssignment,
    deleteAssignment,
  } = useStoreWrapper((state) => ({
    user: state.user,
    edit: state.edit,
    assignments: state.assignment,
    allowAddAssign: state.completed,
    editIndex: state.editIndex,
    clearState: state.clearState,
    updateField: state.updateField,
    setSelectedUserFacilities: state.setSelectedUserFacilities,
    addNewAssignment: state.addAssignment,
    updateAssignment: state.updateAssignment,
    editAssignment: state.editAssignment,
    deleteAssignment: state.removeAssignment,
  }));

  const { getSystemFacilities, getSystemRoles, getSystemProfiles } =
    useAssignment({ profileId, userId: user?.userId });
  const { updateFacilityData } = useUser();
  const navigate = useNavigate();

  // ------- get system facilities --------
  const { isLoading: facilitiesLoading, data: facilitiesData } =
    getSystemFacilities;

  // -------- get system roles --------
  const { isLoading: rolesLoading, data: rolesData } = getSystemRoles;

  // ------- get system profiles --------
  const { isLoading: profilesLoading, data: profilesData } = getSystemProfiles;

  // ------- permissions hook -----------
  const { isPermitted } = usePermission();
  const { isLoading: updating } = updateFacilityData;

  /**
   * Form validation config
   */
  const formik = useFormik({
    initialValues: {
      facility: { facilityName: "", facilityId: "" },
      role: { roleName: "", roleId: "" },
      profile: { profileName: "", profileId: "" },
    },
    validationSchema: validateAssignment(),
    onSubmit: async (values: any) => {
      updateAssignment(
        {
          ...values,
          profile: { ...values.profile, permissions: permissionList },
          completed: true,
        },
        editIndex as number
      );
    },
  });

  const updateFacility = async () => {
    try {
      const currAssignment = { ...assignments[0] };
      const { userId: loggedInUserId } = user;
      const request = {
        loggedInUserId,
        ...permissionList,
        roleId: currAssignment.role.roleId,
        profileId: currAssignment.profile.profileId,
        assignmentId: currAssignment.facility?.assignmentId,
      };

      const res = await updateFacilityData.mutateAsync({ ...request });
      setSelectedUserFacilities(res.data?.data?.facilities);
      toast.success(res.data?.message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 2000,
        toastId: "1",
      });
      navigate("/user/users/user-management/role-assignment");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? error?.message,
        toastConfig(toast)
      );
    }
  };

  useEffect(() => {
    if (editIndex !== null) {
      formik.setValues({ ...assignments[editIndex] });
    }
  }, [assignments]);

  useEffect(() => {
    if (edit) {
      getPermissionObject(
        assignments[0].profile.permissions,
        setPermissionList
      );
    }
  }, []);

  useEffect(() => {
    if (editIndex !== null) {
      if (assignments[editIndex] && assignments[editIndex].profile.profileId) {
        setProfileId(assignments[editIndex].profile.profileId);
      }
    }
  }, []);

  const handleFacilityFilterOption = useCallback(
    (option: any, inputValue: string) => {
      const isMatch = isPermitted(PERMISSION_LIST.ADD_USER, option?.value);
      const defaultFilter = option?.label
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return isMatch && defaultFilter;
    },
    [assignments, PERMISSION_LIST]
  );

  const handleProfileFilterOption = useCallback(
    (option: any, inputValue: string) => {
      const isEditGuest =
        edit && option?.data?.profileName === "Guest User" ? false : true;
      const defaultFilter = option?.label
        .toLowerCase()
        .includes(inputValue.toLowerCase());
      return isEditGuest && defaultFilter;
    },
    [assignments, edit]
  );

  return (
    <RolesFormWrapper>
      {assignments.map((obj, ind) => (
        <form className="" key={ind} onSubmit={formik.handleSubmit}>
          <InputandButtonContainer>
            {/* className="grid grid-cols-4 gap-4 items-center" */}
            <FormInputContainer>
              <FormInput
                addWidth
                label="Select Facility"
                name="facility"
                errorMessage={
                  // @ts-ignore
                  formik?.touched?.facility?.facilityName &&
                  !obj.facility.facilityName
                    ? // @ts-ignore
                      formik?.errors?.facility?.facilityName
                    : null
                }
              >
                <StyledRolesMultiSelect
                  data-testid="autocomplete-facility"
                  name="role"
                  options={
                    !facilitiesLoading
                      ? filterFacilities(
                          assignments,
                          facilitiesData?.data?.data?.facilities
                        )
                      : []
                  }
                  getOptionLabel={(option: any) => option?.facilityName}
                  getOptionValue={(option: any) => option?.facilityId}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={customStyles}
                  isDisabled={obj.completed}
                  isSearchable={true}
                  filterOption={handleFacilityFilterOption}
                  onChange={(val: any) => {
                    formik.setFieldValue("facility", val);
                    updateField(ind, val, "facility");
                  }}
                  placeholder="Select Facility"
                  value={obj.facility}
                />
              </FormInput>
              <FormInput
                label="Select Profile"
                name="profile"
                errorMessage={
                  // @ts-ignore
                  formik?.touched?.profile?.profileName &&
                  !obj.profile.profileName
                    ? //  @ts-ignore
                      formik?.errors?.profile?.profileName
                    : null
                }
              >
                <StyledRolesMultiSelect
                  data-testid="autocomplete-profile"
                  name="profile"
                  isDisabled={obj.completed}
                  options={
                    !profilesLoading
                      ? filterGuestProfile(
                          assignments,
                          profilesData?.data?.data
                        )
                      : []
                  }
                  getOptionLabel={(option: any) => option?.profileName}
                  getOptionValue={(option: any) => option?.profileId}
                  filterOption={handleProfileFilterOption}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={customStyles}
                  placeholder="Select Profile"
                  onChange={(val: any) => {
                    setAssignmentPermissions(val.permissions);
                    getPermissionObject(val.permissions, setPermissionList);
                    setProfileId(val?.profileId);
                    formik.setFieldValue("profile", val);
                    formik.setFieldValue("role", null);
                    updateField(ind, val, "profile");
                    clearState("role", ind);
                  }}
                  value={obj.profile}
                />
              </FormInput>
              <FormInput
                label="Select Role"
                name="role"
                errorMessage={
                  // @ts-ignore
                  formik?.touched?.role?.roleName && !obj.role.roleName
                    ? // @ts-ignore
                      formik?.errors?.role?.roleName
                    : null
                }
              >
                <StyledRolesMultiSelect
                  name="role"
                  data-testid="autocomplete-role"
                  options={!rolesLoading ? rolesData?.data?.data : []}
                  getOptionLabel={(option: any) => option?.roleName}
                  getOptionValue={(option: any) => option?.roleId}
                  // sm:w-48
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={customStyles}
                  placeholder="Select Role"
                  onChange={(val: any) => {
                    setOpen(true);
                    setModalTitle(val.roleName);
                    formik.setFieldValue("role", val);
                    updateField(ind, val, "role");
                  }}
                  isDisabled={obj.completed}
                  value={obj.role}
                  // defaultValue={{ ...obj.role }}
                />
              </FormInput>
            </FormInputContainer>
            {/* ------- form actions ------ */}
            {/* className="flex items-center gap-2" */}
            <ActionBtnContainer>
              {obj.completed ? (
                <Button
                  variant="sm"
                  addClass="text-purple-light bg- border-solid border border-purple-light  py-1"
                  // mt-2
                  disabled={!allowAddAssign}
                  type="button"
                  click={(e) => {
                    e.preventDefault();
                    editAssignment({ ...obj, completed: false }, ind);
                    setProfileId(obj.profile.profileId);
                    formik.setValues({ ...obj });
                  }}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  data-testid="complete-btn"
                  variant="sm"
                  type="submit"
                  addClass="text-white-main bg-purple-main mt-2 py-1"
                >
                  Complete
                </Button>
              )}
              {ind > 0 && (
                <Button
                  type="button"
                  variant="sm"
                  addClass="text-purple-light border-solid border border-purple-light  py-1"
                  // mt-2
                  click={() => {
                    deleteAssignment(obj, ind);
                  }}
                >
                  Delete
                </Button>
              )}
            </ActionBtnContainer>
          </InputandButtonContainer>
        </form>
      ))}

      {allowAddAssign && !edit && (
        <Button
          addClass="text-purple-light border-solid mx-auto block border border-purple-light mt-5"
          click={() => {
            addNewAssignment();
            formik.resetForm({});
            setPermissionList({});
          }}
        >
          <BsPlusLg />
          &nbsp;Add assignment
        </Button>
      )}

      {/* ------- action buttons ------- */}
      <div className="mt-10 flex items-center justify-end gap-3">
        {!edit && (
          <Button
            variant="md"
            type="submit"
            click={cb}
            disabled={!allowAddAssign}
            textContent="Continue"
            addClass="bg-purple-main text-lg text-white-main px-8"
          />
        )}

        {edit && (
          <Button
            variant="md"
            click={() => {
              updateFacility();
            }}
            disabled={!allowAddAssign || updating}
            textContent="Update"
            addClass="bg-purple-main text-lg text-white-main px-8"
          />
        )}
      </div>

      {/* ------- assignment modal ------- */}

      <Modal
        data-testid="ass-modal-wrapper"
        open={open}
        close={() => {
          setOpen(false);
        }}
      >
        <AssModal data-cy="ass-modal">
          <button
            className="close-icon"
            onClick={() => {
              setOpen(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && setOpen(false)}
            tabIndex={0}
          >
            <AiOutlineClose />
          </button>
          <span className="mt-4 block text-center font-inter text-xl font-medium text-purple-600">
            Set Permissions for {modalTitle}
          </span>
          {/* ------- permission list -------  */}
          <div className="mt-8">
            {convertObjEntriesToArray(assignmentPermissions).map(
              (ele: any, ind) => (
                <div key={ind} className="mb-4">
                  <AccordionInput
                    ele={ele}
                    setPermissionList={setPermissionList}
                  />
                </div>
              )
            )}
            <Button
              testid="continue-assign-btn"
              addClass="w-full bg-purple-light text-white-main"
              click={() => {
                setOpen(false);
              }}
            >
              Continue
            </Button>
          </div>
        </AssModal>
      </Modal>
    </RolesFormWrapper>
  );
}
export default Roles;
